import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { init as initSpatial } from '@noriginmedia/norigin-spatial-navigation';
import './styles.css';

import { routeTree } from './routeTree.gen';
import { ApiRequestError, setUnauthenticatedHandler } from './api/client.js';
import { restoreSession } from './auth/session.js';
import { startGamepad, watchInputSource } from './gamepad/input.js';
import { captureAffiliate } from './lib/settings.js';
import { startTracking } from './analytics/wire.js';

/**
 * Spatial navigation, set up once.
 *
 * `shouldFocusDOMNode` keeps the browser's own focus in step with the library's, which is what
 * makes a screen reader and a gamepad agree on what is focused.
 */
initSpatial({
  debug: false,
  visualDebug: false,
  shouldFocusDOMNode: true,
  shouldUseNativeEvents: true,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a refusal only wastes time and rate limit.
        if (error instanceof ApiRequestError) {
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function start(): Promise<void> {
  // A referral code in the address is stored before anything else, so it survives a sign in
  // round trip through a payment provider (`01-product-rules.md` section 6).
  captureAffiliate(window.location.search);

  // The apps keep their session token in secure storage, so it is loaded before the first
  // request goes out.
  await restoreSession();

  // Browsing signed out is the normal case now, so a 401 is only worth acting on when there
  // was a session a moment ago: that is an expired session, and signing in again fixes it.
  // Without this, the first `/me` of every visit would throw a visitor at the sign in screen.
  setUnauthenticatedHandler(() => {
    const hadSession = Boolean(queryClient.getQueryData(['me']));
    queryClient.setQueryData(['me'], null);
    if (hadSession) void router.navigate({ to: '/signin' });
  });

  void startGamepad();
  watchInputSource();

  // After `restoreSession`, so the first page view of a launch is attributed to whoever is
  // signed in rather than landing a moment before the session is known and counting as anonymous.
  startTracking(router);

  const container = document.getElementById('root');
  if (!container) throw new Error('the root element is missing from index.html');

  createRoot(container).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void start();
